import { useIsFocused } from '@react-navigation/native'
import { usePathname } from 'expo-router'
import { Activity, useMemo, type ReactNode } from 'react'
import { View } from 'react-native'

import { ErrorBoundary } from '@/src/config/sentry'
import { Tabs } from '@/src/elements/navigation/Tabs'
import { getIsLoggedIn } from '@/src/features/auth/utils/getIsLoggedIn'
import { useAddBottom } from '@/src/general/components/BottomContext'
import { useDimensions } from '@/src/general/hooks/useDimensions'

import { ScreenErrorFallback } from '../../screen-error-fallback/ScreenErrorFallback'
import { CommonLayout } from '../../templates/CommonLayout'
import { useTabNavigationData } from './hooks/useTabNavigationData'
import type { TTabProps } from './Tab.types'
import { TAB_BAR_HEIGHT, TabBar } from './TabBar'

const noTabsOnPath = [/^\/messages\/[^/]+$/, /^\/messages\/pray$/, /^\/messages\/[a-zA-Z0-9-]+\/pray$/]

const TabScreenActivity = ({ children }: { children: ReactNode }): ReactNode => {
  const isFocused = useIsFocused()

  return <Activity mode={isFocused ? 'visible' : 'hidden'}>{children}</Activity>
}

export const Tab = ({ activePage }: TTabProps): ReactNode => {
  const { lessThan } = useDimensions()
  const pathname = usePathname()
  const showTabBar = useMemo(() => lessThan.md, [lessThan.md])
  const isLoggedIn = getIsLoggedIn()

  useAddBottom('TabBar', showTabBar ? TAB_BAR_HEIGHT : 0)

  const navigationData = useTabNavigationData()

  const paths = !isLoggedIn ? noTabsOnPath : [...noTabsOnPath, /^\/settings\/.*$/]

  return (
    <Tabs
      // https://reactnavigation.org/docs/bottom-tab-navigator/#backbehavior
      backBehavior="history"
      tabBar={(props) =>
        showTabBar &&
        paths.every((regex) => !regex.test(pathname)) && (
          <TabBar className="bg-white" activePage={activePage} navigationData={navigationData} {...props} />
        )
      }
      screenOptions={{
        headerShown: false,
        freezeOnBlur: false,
        // FIXME: Setting lazy to true, breaks web navigation
        lazy: false,
      }}
      screenLayout={({ children }) => (
        <ErrorBoundary
          fallback={({ resetError }) => (
            <CommonLayout>
              <View className="mt-40">
                <ScreenErrorFallback resetError={resetError} />
              </View>
            </CommonLayout>
          )}
        >
          <TabScreenActivity>{children}</TabScreenActivity>
        </ErrorBoundary>
      )}
    >
      {Object.keys(navigationData).map((routeName) => (
        <Tabs.Screen
          key={routeName}
          name={routeName === 'intentions' ? 'intentions/index' : routeName}
          options={{ href: '/' + routeName }}
        />
      ))}
    </Tabs>
  )
}
